import { useQuery, useQueryClient, useMutation, useQueries } from "react-query";
import { axiosAuth } from "./axiosConfig";

/**
 * Fetch recent samples for a given date range and location.
 */

/**
 * Fetch recent samples for a given date range and location.
 * @param {{start: string, end: string}} dateRange
 * @param {string} selectedLocation
 * @returns {object} React Query result
 */
export const useRecentSamples = (
  dateRange,
  selectedLocation,
  selectedCustomer
) =>
  useQuery(
    ["byLoc", "recentSamples", dateRange, selectedLocation, selectedCustomer],
    async () => {
      try {
        const { data } = await axiosAuth.get(`/recent-samples/recentSamples`, {
          params: {
            paststart: dateRange.start,
            pastend: dateRange.end,
            selectedLocation,
            selectedCustomer,
          },
        });
        return data;
      } catch (error) {
        console.error("Error fetching recent samples:", error);
        throw error;
      }
    }
  );

/**
 * Fetch a single sample by ID.
 * @param {string} id
 * @returns {object} React Query result
 */
export const useSample = (id) =>
  useQuery(["sample", id], async () => {
    try {
      const { data } = await axiosAuth.get("/view-edit-sample/get-sample", {
        params: { id },
      });
      return data;
    } catch (error) {
      console.error("Error fetching sample:", error);
      throw error;
    }
  });

/**
 * Fetch scores for current and previous date ranges and location.
 * @param {string} ldate
 * @param {string} hdate
 * @param {string} prevldate
 * @param {string} prevhdate
 * @param {string} selectedLocation
 * @returns {object} React Query result
 */
export const useScores = (
  ldate,
  hdate,
  prevldate,
  prevhdate,
  selectedLocation,
  selectedCustomer
) =>
  useQuery(
    [
      "byLoc",
      "scores",
      ldate,
      hdate,
      prevldate,
      prevhdate,
      selectedLocation,
      selectedCustomer,
    ],
    async () => {
      try {
        const { data } = await axiosAuth.get("/body-stats/scores", {
          params: {
            ldate,
            hdate,
            prevldate,
            prevhdate,
            selectedLocation,
            selectedCustomer,
          },
        });
        return data;
      } catch (error) {
        console.error("Error fetching scores:", error);
        throw error;
      }
    }
  );

/**
 * Fetch adverse reactions plot data for a date and location.
 * @param {string} ldate
 * @param {string} selectedLocation
 * @returns {object} React Query result
 */
export const useAdverseReactionsPlotData = (
  ldate,
  selectedLocation,
  selectedCustomer
) => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return useQuery(
    [
      "byLoc",
      "adverseReactionsPlotData",
      ldate,
      userTimezone,
      selectedLocation,
      selectedCustomer,
    ],
    async () => {
      try {
        const { data } = await axiosAuth.get(
          "/body-stats/adverseReactionsPlotData",
          {
            params: { ldate, userTimezone, selectedLocation, selectedCustomer },
          }
        );
        return data;
      } catch (error) {
        console.error("Error fetching adverse reactions plot data:", error);
        throw error;
      }
    }
  );
};
/**
 * Fetch common drugs for a date, drug, and location.
 * @param {string} ldate
 * @param {string} drug
 * @param {string} selectedLocation
 * @returns {object} React Query result
 */
export const useCommonDrugs = (
  ldate,
  drug,
  selectedLocation,
  selectedCustomer
) =>
  useQuery(
    ["byLoc", "commonDrugs", ldate, drug, selectedLocation, selectedCustomer],
    async () => {
      try {
        const { data } = await axiosAuth.get("/body-stats/commonDrugs", {
          params: { ldate, drug, selectedLocation, selectedCustomer },
        });
        return data;
      } catch (error) {
        console.error("Error fetching common drugs:", error);
        throw error;
      }
    }
  );

/**
 * Mutation hook to edit a sample.
 * @param {string} id
 * @returns {object} React Mutation result
 */
export const useSampleEdit = (id) => {
  const queryClient = useQueryClient();
  return useMutation(editSample, {
    onSuccess: () => {
      queryClient.invalidateQueries(["sample", id]);
      queryClient.invalidateQueries("recentSamples");
    },
    onError: (error) => {
      console.error("Error editing sample:", error);
    },
  });
};

/**
 * Edit a sample by ID and data.
 */
/**
 * Edit a sample by ID and data.
 * @param {{id: string, data: object}} param0
 * @returns {object} Edited sample data
 */
export const editSample = async ({ id, data }) => {
  try {
    const res = await axiosAuth.patch("/view-edit-sample/edit-sample", {
      id,
      data,
    });
    return res.data;
  } catch (error) {
    console.error("Error editing sample:", error);
    throw error;
  }
};

/**
 * Fetch reference spectrum for findings.
 * @param {Array} findings
 * @returns {object} { isLoading, isError, isSuccess, results }
 */
export const useReferenceSpectrum = (findings) => {
  const results = useQueries(
    findings.map((finding) => ({
      queryKey: ["getReferenceSpectrum", finding.collection, finding.analyte],
      queryFn: async () => {
        if (!finding.analyte || finding.analyte === "Inconclusive") {
          return null;
        }
        try {
          const { data } = await axiosAuth.get(
            "/view-edit-sample/get-reference-spectrum",
            {
              params: {
                collection: finding.collection,
                analyte: finding.analyte,
              },
            }
          );
          if (typeof data === "object") {
            data.scanType = finding.collection;
          }
          return data;
        } catch (error) {
          if (error.response && error.response.status === 404) {
            // Return null or a custom object for 404
            return null;
          }
          console.error("Error fetching reference spectrum:", error);
          throw error;
        }
      },
    })),
    {
      enabled:
        !!findings &&
        findings.length > 0 &&
        findings[0].analyte &&
        findings[0].analyte !== "Inconclusive",
      retry: false,
    }
  );

  const isLoading = results.some((query) => query.isLoading);
  const isError = results.some((query) => query.isError);
  const isSuccess =
    results.length !== 0 && results.every((query) => query.isSuccess);

  if (isError) {
    console.error("An error occurred while fetching reference spectrum data.");
  }

  return { isLoading, isError, isSuccess, results };
};

/**
 * Validate a sample.
 * @param {object} sample
 * @param {function} errorLogger
 * @returns {object} React Query result
 */
export const useValidateSample = (sample, errorLogger) =>
  useQuery(
    ["validateData", sample],
    async () => {
      try {
        return await axiosAuth.post(`/new-sample/verify-sample`, sample);
      } catch (error) {
        console.error("Error validating sample:", error);
        throw error;
      }
    },
    {
      // Don't run query by default
      timeout: 1000,
      enabled: false,
      onError: errorLogger,
    }
  );

export const useGetSubstanceInfo = (result, country) => {
  return useQuery(
    ["getSubstanceInfo", result, country],
    async () => {
      if (!result) {
        return null; // Return null or handle the case when result is not provided
      }
      if (result === "Inconclusive" || result === "System Error") {
        return null;
      }
      const { data } = await axiosAuth.get(
        "/view-edit-sample/get-substance-info",
        {
          params: {
            result: result,
            country: country,
          },
        }
      );
      return data;
    },
    {
      enabled: !!result,
      retry: false,
    }
  );
};
